import React, {
  createContext,
  useContext,
  useState,
  useRef,
  useEffect,
  useCallback,
  useMemo,
} from 'react';
import { PropTypes } from '../../../utils/propTypes';
import { PopoverContent } from '../PopoverMenu';
import { Icon } from '../Icon';
import styles from './DropdownMenu.module.css';

/**
 * DropdownMenu — a shadcn-style compound menu, reimplemented in Athena's idiom
 * (JSX + CSS Modules + 2.0 tokens). No Radix/Base-UI dependency: each menu /
 * submenu panel renders through the compound PopoverContent (bare mode), which
 * itself sits on the shared imperative Popover primitive (portal / position /
 * flip / click-outside). Submenus open to the RIGHT via the primitive's
 * additive 'right-start' placement.
 *
 * Styled with `--menu-*` tokens (globals.css); the panel surface comes from
 * `--popover-content-*`. Preview-only for now (globals.css isn't imported into
 * the app). Not wired into any live feature yet.
 *
 * Full family: DropdownMenu / DropdownMenuTrigger / DropdownMenuContent /
 * DropdownMenuGroup / DropdownMenuLabel / DropdownMenuItem /
 * DropdownMenuCheckboxItem / DropdownMenuRadioGroup / DropdownMenuRadioItem /
 * DropdownMenuSeparator / DropdownMenuShortcut / DropdownMenuSub /
 * DropdownMenuSubTrigger / DropdownMenuSubContent / DropdownMenuPortal.
 */

const MenuContext = createContext(null);          // { open, setOpen, triggerRef }
const RadioGroupContext = createContext(null);    // { value, onValueChange }
const SubContext = createContext(null);           // { subOpen, setSubOpen, subTriggerRef }

const mergeRefs = (...refs) => (node) => {
  refs.forEach((ref) => {
    if (!ref) return;
    if (typeof ref === 'function') ref(node);
    else ref.current = node;
  });
};

/* Roving-focus keyboard handling shared by the menu + submenu lists. */
function handleMenuKeyDown(e, listRef, { onClose, onArrowLeft } = {}) {
  const list = listRef.current;
  if (!list) return;
  const items = Array.from(list.querySelectorAll('[role^="menuitem"]:not(:disabled)'));
  if (items.length === 0) return;
  const idx = items.indexOf(document.activeElement);
  switch (e.key) {
    case 'ArrowDown':
      e.preventDefault();
      (items[idx + 1] || items[0]).focus();
      break;
    case 'ArrowUp':
      e.preventDefault();
      (items[idx - 1] || items[items.length - 1]).focus();
      break;
    case 'Home':
      e.preventDefault();
      items[0].focus();
      break;
    case 'End':
      e.preventDefault();
      items[items.length - 1].focus();
      break;
    case 'Escape':
      e.preventDefault();
      e.stopPropagation();
      onClose?.();
      break;
    case 'ArrowLeft':
      if (onArrowLeft) {
        e.preventDefault();
        e.stopPropagation();
        onArrowLeft();
      }
      break;
    default:
      break;
  }
}

/* Focus the first enabled item inside a list once it opens. */
function useFocusFirstOnOpen(open, listRef) {
  useEffect(() => {
    if (!open) return undefined;
    const raf = requestAnimationFrame(() => {
      listRef.current?.querySelector('[role^="menuitem"]:not(:disabled)')?.focus();
    });
    return () => cancelAnimationFrame(raf);
  }, [open, listRef]);
}

// DropdownMenu — ROOT. Owns open state + trigger ref; provides context.
const DropdownMenu = ({ open: controlledOpen, defaultOpen = false, onOpenChange, children }) => {
  const isControlled = controlledOpen !== undefined;
  const [uncontrolledOpen, setUncontrolledOpen] = useState(defaultOpen);
  const open = isControlled ? controlledOpen : uncontrolledOpen;
  const triggerRef = useRef(null);

  const setOpen = useCallback(
    (next) => {
      const value = typeof next === 'function' ? next(open) : next;
      if (!isControlled) setUncontrolledOpen(value);
      onOpenChange?.(value);
    },
    [isControlled, onOpenChange, open]
  );

  const ctx = useMemo(() => ({ open, setOpen, triggerRef }), [open, setOpen]);
  return <MenuContext.Provider value={ctx}>{children}</MenuContext.Provider>;
};

// DropdownMenuTrigger — opens the menu. Supports `render={<Button/>}` (base-ui
// docs: trigger children become the element's content), `asChild` (Radix-style
// alias, merges onto a single child element), or its own <button>.
const DropdownMenuTrigger = ({ asChild = false, render, children, className = '', onClick, ...props }) => {
  const { open, setOpen, triggerRef } = useContext(MenuContext);
  const handleClick = (ownOnClick) => (e) => {
    ownOnClick?.(e);
    onClick?.(e);
    setOpen((o) => !o);
  };
  const withTriggerProps = (el, content) =>
    React.cloneElement(
      el,
      {
        ...props,
        ref: mergeRefs(triggerRef, el.ref),
        className: [el.props.className, className].filter(Boolean).join(' ') || undefined,
        onClick: handleClick(el.props.onClick),
        'aria-haspopup': 'menu',
        'aria-expanded': open,
      },
      content
    );

  if (render && React.isValidElement(render)) {
    return withTriggerProps(render, children);
  }
  if (asChild && React.isValidElement(children)) {
    return withTriggerProps(children, children.props.children);
  }
  return (
    <button
      ref={triggerRef}
      type="button"
      className={className}
      onClick={handleClick()}
      aria-haspopup="menu"
      aria-expanded={open}
      {...props}
    >
      {children}
    </button>
  );
};

// DropdownMenuContent — the menu panel (PopoverContent bare), role="menu" with
// roving-focus keyboard nav. Width matches the trigger (min --menu-content-min-width).
const DropdownMenuContent = ({
  children,
  className = '',
  side = 'bottom',
  align = 'start',
  sideOffset = 4,
}) => {
  const { open, setOpen, triggerRef } = useContext(MenuContext);
  const [minWidth, setMinWidth] = useState(undefined);
  const listRef = useRef(null);

  useEffect(() => {
    if (open && triggerRef.current) {
      setMinWidth(Math.round(triggerRef.current.getBoundingClientRect().width));
    }
  }, [open, triggerRef]);

  useFocusFirstOnOpen(open, listRef);

  const onKeyDown = (e) =>
    handleMenuKeyDown(e, listRef, {
      onClose: () => {
        setOpen(false);
        triggerRef.current?.focus?.();
      },
    });

  return (
    <PopoverContent
      bare
      open={open}
      triggerRef={triggerRef}
      onClose={() => setOpen(false)}
      side={side}
      align={align}
      sideOffset={sideOffset}
      minWidth={minWidth}
    >
      <div ref={listRef} role="menu" className={`${styles.menu} ${className}`} onKeyDown={onKeyDown}>
        {children}
      </div>
    </PopoverContent>
  );
};

// DropdownMenuGroup — groups related items (role="group").
const DropdownMenuGroup = ({ children, className = '' }) => (
  <div role="group" className={className}>
    {children}
  </div>
);

// DropdownMenuLabel — non-interactive section heading; `inset` aligns with items
// that have a leading indicator.
const DropdownMenuLabel = ({ children, inset = false, className = '' }) => (
  <div className={`${styles.label} ${inset ? styles.inset : ''} ${className}`}>{children}</div>
);

// DropdownMenuItem — a selectable action; closes the menu on select.
// `variant='destructive'` for dangerous actions; `inset` to align with checkbox/radio rows.
const DropdownMenuItem = ({
  children,
  className = '',
  inset = false,
  variant = 'default',
  disabled = false,
  onSelect,
  onClick,
  ...props
}) => {
  const { setOpen } = useContext(MenuContext) || {};
  const handle = (e) => {
    if (disabled) return;
    onClick?.(e);
    onSelect?.(e);
    setOpen?.(false);
  };
  return (
    <button
      type="button"
      role="menuitem"
      disabled={disabled}
      className={[
        styles.item,
        variant === 'destructive' && styles.itemDestructive,
        inset && styles.inset,
        className,
      ]
        .filter(Boolean)
        .join(' ')}
      onClick={handle}
      {...props}
    >
      {children}
    </button>
  );
};

// DropdownMenuCheckboxItem — toggles a boolean; keeps the menu open.
const DropdownMenuCheckboxItem = ({
  children,
  checked = false,
  onCheckedChange,
  disabled = false,
  className = '',
}) => (
  <button
    type="button"
    role="menuitemcheckbox"
    aria-checked={checked}
    disabled={disabled}
    className={`${styles.checkboxItem} ${className}`}
    onClick={() => !disabled && onCheckedChange?.(!checked)}
  >
    <span className={styles.indicator}>{checked && <Icon name="check" className={styles.check} />}</span>
    {children}
  </button>
);

// DropdownMenuRadioGroup — single-select group; provides value/onValueChange.
const DropdownMenuRadioGroup = ({ value, onValueChange, children, className = '' }) => {
  const ctx = useMemo(() => ({ value, onValueChange }), [value, onValueChange]);
  return (
    <RadioGroupContext.Provider value={ctx}>
      <div role="group" className={className}>
        {children}
      </div>
    </RadioGroupContext.Provider>
  );
};

// DropdownMenuRadioItem — one radio option; selecting it closes the menu.
const DropdownMenuRadioItem = ({ children, value, disabled = false, className = '' }) => {
  const group = useContext(RadioGroupContext) || {};
  const { setOpen } = useContext(MenuContext) || {};
  const selected = group.value === value;
  return (
    <button
      type="button"
      role="menuitemradio"
      aria-checked={selected}
      disabled={disabled}
      className={`${styles.radioItem} ${className}`}
      onClick={() => {
        if (disabled) return;
        group.onValueChange?.(value);
        setOpen?.(false);
      }}
    >
      <span className={styles.indicator}>{selected && <Icon name="check" className={styles.check} />}</span>
      {children}
    </button>
  );
};

// DropdownMenuSeparator — thin divider between sections.
const DropdownMenuSeparator = ({ className = '' }) => (
  <div role="separator" aria-hidden="true" className={`${styles.separator} ${className}`} />
);

// DropdownMenuShortcut — right-aligned muted keyboard hint inside an item.
const DropdownMenuShortcut = ({ children, className = '' }) => (
  <span className={`${styles.shortcut} ${className}`}>{children}</span>
);

// DropdownMenuSub — wraps a sub-trigger + sub-content; owns the submenu state.
const DropdownMenuSub = ({ children }) => {
  const [subOpen, setSubOpen] = useState(false);
  const subTriggerRef = useRef(null);
  const ctx = useMemo(() => ({ subOpen, setSubOpen, subTriggerRef }), [subOpen]);
  return <SubContext.Provider value={ctx}>{children}</SubContext.Provider>;
};

// DropdownMenuSubTrigger — the row that opens a submenu (hover / click / ArrowRight).
const DropdownMenuSubTrigger = ({ children, inset = false, disabled = false, className = '' }) => {
  const { subOpen, setSubOpen, subTriggerRef } = useContext(SubContext);
  return (
    <button
      ref={subTriggerRef}
      type="button"
      role="menuitem"
      aria-haspopup="menu"
      aria-expanded={subOpen}
      disabled={disabled}
      className={[styles.subTrigger, subOpen && styles.subTriggerOpen, inset && styles.inset, className]
        .filter(Boolean)
        .join(' ')}
      onClick={() => setSubOpen((o) => !o)}
      onMouseEnter={() => setSubOpen(true)}
      onKeyDown={(e) => {
        if (e.key === 'ArrowRight' || e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          setSubOpen(true);
        }
      }}
    >
      {children}
      <Icon name="chevron-right" className={styles.chevron} />
    </button>
  );
};

// DropdownMenuSubContent — the submenu panel; opens to the right, ArrowLeft/Escape
// closes back to the sub-trigger.
const DropdownMenuSubContent = ({ children, className = '' }) => {
  const { subOpen, setSubOpen, subTriggerRef } = useContext(SubContext);
  const listRef = useRef(null);

  useFocusFirstOnOpen(subOpen, listRef);

  const closeToTrigger = () => {
    setSubOpen(false);
    subTriggerRef.current?.focus();
  };
  const onKeyDown = (e) =>
    handleMenuKeyDown(e, listRef, { onClose: closeToTrigger, onArrowLeft: closeToTrigger });

  return (
    <PopoverContent
      bare
      open={subOpen}
      triggerRef={subTriggerRef}
      onClose={() => setSubOpen(false)}
      side="right"
      align="start"
      minWidth={96}
      panelClassName={styles.subPanel}
    >
      <div
        ref={listRef}
        role="menu"
        className={`${styles.menu} ${styles.subMenu} ${className}`}
        onKeyDown={onKeyDown}
      >
        {children}
      </div>
    </PopoverContent>
  );
};

// DropdownMenuPortal — API-compat passthrough (PopoverContent already portals).
const DropdownMenuPortal = ({ children }) => <>{children}</>;

DropdownMenu.propTypes = {
  open: PropTypes.bool,
  defaultOpen: PropTypes.bool,
  onOpenChange: PropTypes.func,
  children: PropTypes.node,
};
DropdownMenuTrigger.propTypes = {
  asChild: PropTypes.bool,
  render: PropTypes.node,
  children: PropTypes.node,
  className: PropTypes.string,
  onClick: PropTypes.func,
};
DropdownMenuContent.propTypes = {
  children: PropTypes.node,
  className: PropTypes.string,
  side: PropTypes.string,
  align: PropTypes.string,
  sideOffset: PropTypes.number,
};
DropdownMenuGroup.propTypes = { children: PropTypes.node, className: PropTypes.string };
DropdownMenuLabel.propTypes = { children: PropTypes.node, inset: PropTypes.bool, className: PropTypes.string };
DropdownMenuItem.propTypes = {
  children: PropTypes.node,
  className: PropTypes.string,
  inset: PropTypes.bool,
  variant: PropTypes.string,
  disabled: PropTypes.bool,
  onSelect: PropTypes.func,
  onClick: PropTypes.func,
};
DropdownMenuCheckboxItem.propTypes = {
  children: PropTypes.node,
  checked: PropTypes.bool,
  onCheckedChange: PropTypes.func,
  disabled: PropTypes.bool,
  className: PropTypes.string,
};
DropdownMenuRadioGroup.propTypes = {
  value: PropTypes.string,
  onValueChange: PropTypes.func,
  children: PropTypes.node,
  className: PropTypes.string,
};
DropdownMenuRadioItem.propTypes = {
  children: PropTypes.node,
  value: PropTypes.string,
  disabled: PropTypes.bool,
  className: PropTypes.string,
};
DropdownMenuSeparator.propTypes = { className: PropTypes.string };
DropdownMenuShortcut.propTypes = { children: PropTypes.node, className: PropTypes.string };
DropdownMenuSub.propTypes = { children: PropTypes.node };
DropdownMenuSubTrigger.propTypes = {
  children: PropTypes.node,
  inset: PropTypes.bool,
  disabled: PropTypes.bool,
  className: PropTypes.string,
};
DropdownMenuSubContent.propTypes = { children: PropTypes.node, className: PropTypes.string };
DropdownMenuPortal.propTypes = { children: PropTypes.node };

export {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuGroup,
  DropdownMenuLabel,
  DropdownMenuItem,
  DropdownMenuCheckboxItem,
  DropdownMenuRadioGroup,
  DropdownMenuRadioItem,
  DropdownMenuSeparator,
  DropdownMenuShortcut,
  DropdownMenuSub,
  DropdownMenuSubTrigger,
  DropdownMenuSubContent,
  DropdownMenuPortal,
};
